/**
 * The reading-body markdown dialect — ONE renderer for every web surface that
 * shows a reading body (feat-298 spec §5, revised).
 *
 * Moved verbatim out of `ReadingDetailForm.tsx` (feat-166), where it was the
 * authoring preview. The Flutter reader
 * (`RestaurantAI-Flutter/lib/widgets/learn/reading_item_view.dart`) mirrors its
 * regexes exactly, so the curriculum editor's preview, the `/quiz-review` reading
 * card and the learner's phone all show the same thing. Do not copy it and do
 * not swap in a general-purpose markdown library: either would drift from what
 * learners see (e.g. `react-markdown` collapses single newlines).
 *
 * Dialect: `#`–`###` headings (a one-line block), `- ` bullet lists (every line
 * of the block), blank-line-separated paragraphs, single newlines as `<br>`,
 * `**bold**`, `*italic*`, `[label](url)` links for http(s) only, and (feat-347)
 * GFM pipe tables: a header row plus a `---` / `:---` / `:---:` / `---:`
 * delimiter row with the same cell count, optional outer pipes, `\|` as a
 * literal pipe, ragged body rows padded / truncated, ending at the first line
 * with no `|`. A table may share a blank-line block with heading / paragraph
 * lines; those render by the usual rules as sibling sub-blocks. Wide tables
 * scroll horizontally inside their own box.
 *
 * SAFE by construction: React nodes only — never `dangerouslySetInnerHTML` — so
 * `<script>` / `<img onerror>` in an authored or translated body are text. There
 * is no image syntax, so `![](…)` never fetches anything (design Q4).
 *
 * The two props beyond `source` exist only for the review card; their defaults
 * are the editor's behaviour, byte for byte.
 */
import React from 'react';

/** The editor preview's empty state. */
export const MARKDOWN_PREVIEW_EMPTY_TEXT = 'Nothing to preview yet.';
/** The editor preview's wrapper classes. */
export const MARKDOWN_PREVIEW_CLASS = 'space-y-2 text-sm text-ink leading-relaxed';

/** Inline **bold** / *italic* / [label](url) → React nodes. No HTML injection: we
 *  tokenize and emit <strong>/<em>/<a>/text only, and only http(s) hrefs render as
 *  links (others fall back to the label text). Unmatched markers render literally. */
export function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Split on **bold**, *italic*, and [label](url) while keeping the delimiters.
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g);
  parts.forEach((part, i) => {
    if (!part) return;
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      nodes.push(<strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>);
    } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      nodes.push(<em key={`${keyPrefix}-i${i}`}>{part.slice(1, -1)}</em>);
    } else if (link) {
      const [, label, href] = link;
      if (/^https?:\/\//i.test(href)) {
        nodes.push(
          <a
            key={`${keyPrefix}-a${i}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-reading underline"
          >
            {label}
          </a>,
        );
      } else {
        nodes.push(<React.Fragment key={`${keyPrefix}-a${i}`}>{label}</React.Fragment>);
      }
    } else {
      nodes.push(<React.Fragment key={`${keyPrefix}-t${i}`}>{part}</React.Fragment>);
    }
  });
  return nodes;
}

// ============================================================
// feat-347 — GFM pipe tables (spec.md "Grammar"; mirrored in the Flutter
// reader's `_MarkdownBody`, checked by shared fixtures in both test suites).
// ============================================================

export type TableAlign = 'left' | 'center' | 'right';

export interface MarkdownTable {
  header: string[];
  aligns: TableAlign[];
  /** Body rows, each padded / truncated to the header's cell count. */
  rows: string[][];
}

/** One piece of a blank-line block: a table, or a run of non-table lines that
 *  renders through the existing heading / list / paragraph rules. */
export type MarkdownSubBlock =
  | { kind: 'table'; table: MarkdownTable }
  | { kind: 'other'; lines: string[] };

const DELIMITER_CELL = /^\s*:?-{3,}:?\s*$/;

/** True if `line` contains a pipe that is not backslash-escaped. */
function hasUnescapedPipe(line: string): boolean {
  return /(^|[^\\])\|/.test(line);
}

/** Split a table row into trimmed cells. Leading / trailing pipes are optional;
 *  `\|` is a literal pipe inside a cell. */
export function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '\\' && s[i + 1] === '|') {
      cur += '|';
      i++;
    } else if (ch === '|') {
      cells.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  cells.push(cur.trim());
  return cells;
}

/** The delimiter row's alignments, or null if `line` is not a delimiter row. */
export function parseDelimiterRow(line: string): TableAlign[] | null {
  if (!hasUnescapedPipe(line)) return null;
  const cells = splitTableRow(line);
  if (!cells.every((c) => DELIMITER_CELL.test(c))) return null;
  return cells.map((c) => {
    const l = c.startsWith(':');
    const r = c.endsWith(':');
    return l && r ? 'center' : r ? 'right' : 'left';
  });
}

/** Split one blank-line block into table / non-table sub-blocks. A table starts
 *  at a header line (containing a pipe) followed by a delimiter row with the same
 *  cell count, and runs until the first line with no pipe or the block end.
 *  A block with no table comes back as a single `other` sub-block of its lines. */
export function splitTableSubBlocks(lines: string[]): MarkdownSubBlock[] {
  const out: MarkdownSubBlock[] = [];
  let pending: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const aligns = i + 1 < lines.length && hasUnescapedPipe(lines[i]) ? parseDelimiterRow(lines[i + 1]) : null;
    const header = aligns ? splitTableRow(lines[i]) : null;
    if (aligns && header && header.length === aligns.length) {
      if (pending.length) out.push({ kind: 'other', lines: pending });
      pending = [];
      const rows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && lines[j].includes('|')) {
        const cells = splitTableRow(lines[j]).slice(0, header.length);
        while (cells.length < header.length) cells.push('');
        rows.push(cells);
        j++;
      }
      out.push({ kind: 'table', table: { header, aligns, rows } });
      i = j;
    } else {
      pending.push(lines[i]);
      i++;
    }
  }
  if (pending.length) out.push({ kind: 'other', lines: pending });
  return out;
}

export interface MarkdownPreviewProps {
  source: string;
  /** Shown when `source` has no non-blank block. `null` renders nothing.
   *  Default: the editor's "Nothing to preview yet." */
  emptyText?: string | null;
  /** Wrapper classes. Default: the editor's. The review card passes its own so
   *  the body keeps the card's ink/leading (and the 4-line clamp height). */
  className?: string;
}

/** Block-level markdown → React. Handles #/##/### headings, `- ` bullet lists,
 *  pipe tables, and blank-line-separated paragraphs. Deliberately small (authoring feedback). */
export const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({
  source,
  emptyText = MARKDOWN_PREVIEW_EMPTY_TEXT,
  className = MARKDOWN_PREVIEW_CLASS,
}) => {
  const blocks = source.split(/\n\s*\n/).filter((b) => b.trim().length > 0);
  if (blocks.length === 0) {
    return emptyText == null ? null : <p className="text-inkMuted italic">{emptyText}</p>;
  }
  return (
    <div className={className}>
      {blocks.flatMap((block, bi) => {
        const subs = splitTableSubBlocks(block.split('\n'));
        // A block with no table renders exactly as before (same nodes, same keys).
        if (subs.length === 1 && subs[0].kind === 'other') return [renderTextBlock(subs[0].lines, `${bi}`)];
        return subs.map((sb, si) =>
          sb.kind === 'table' ? renderTable(sb.table, `${bi}.${si}`) : renderTextBlock(sb.lines, `${bi}.${si}`),
        );
      })}
    </div>
  );
};

/** A non-table (sub-)block: bullet list, one-line heading, or paragraph. */
function renderTextBlock(lines: string[], bk: string): React.ReactNode {
  // Bullet list: every non-empty line starts with "- ".
  const isList = lines.every((l) => /^\s*-\s+/.test(l));
  if (isList) {
    return (
      <ul key={`bl-${bk}`} className="list-disc pl-5 space-y-0.5">
        {lines.map((l, li) => (
          <li key={`bl-${bk}-${li}`}>{renderInline(l.replace(/^\s*-\s+/, ''), `bl-${bk}-${li}`)}</li>
        ))}
      </ul>
    );
  }
  const h = /^(#{1,3})\s+(.*)$/.exec(lines[0]);
  if (h && lines.length === 1) {
    const level = h[1].length;
    const cls = level === 1 ? 'text-lg font-bold' : level === 2 ? 'text-base font-bold' : 'text-sm font-semibold';
    return (
      <p key={`h-${bk}`} className={cls}>
        {renderInline(h[2], `h-${bk}`)}
      </p>
    );
  }
  return (
    <p key={`p-${bk}`}>
      {lines.map((l, li) => (
        <React.Fragment key={`p-${bk}-${li}`}>
          {li > 0 && <br />}
          {renderInline(l, `p-${bk}-${li}`)}
        </React.Fragment>
      ))}
    </p>
  );
}

// Class strings copied verbatim from design/01-table-web.html (feat-347).
// `w-max` on the table + min/max-w on every cell are a pair: without `w-max`
// the browser squeezes every column to its 96px floor.
export const TABLE_WRAPPER_CLASS = 'relative w-fit max-w-full overflow-x-auto rounded-lg border border-softBorder bg-white';
export const TABLE_CLASS = 'w-max border-separate border-spacing-0 text-sm leading-snug';
export const TABLE_TH_CLASS =
  'min-w-[96px] max-w-[200px] px-3 py-2 align-top font-bold bg-cream-dark border-l border-softBorder first:border-l-0';
export const TABLE_TD_CLASS =
  'min-w-[96px] max-w-[200px] px-3 py-2 align-top border-l border-t border-softBorder first:border-l-0';
const ALIGN_CLASS: Record<TableAlign, string> = {
  left: ' text-left',
  center: ' text-center',
  right: ' text-right',
};

/** A pipe table in its own horizontally scrollable box. */
function renderTable(t: MarkdownTable, bk: string): React.ReactNode {
  return (
    <div key={`t-${bk}`} className={TABLE_WRAPPER_CLASS}>
      <table className={TABLE_CLASS}>
        <thead>
          <tr>
            {t.header.map((c, ci) => (
              <th key={`t-${bk}-0-${ci}`} className={TABLE_TH_CLASS + ALIGN_CLASS[t.aligns[ci]]}>
                {renderInline(c, `t-${bk}-0-${ci}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {t.rows.map((row, ri) => (
            <tr key={`t-${bk}-${ri + 1}`}>
              {row.map((c, ci) => (
                <td key={`t-${bk}-${ri + 1}-${ci}`} className={TABLE_TD_CLASS + ALIGN_CLASS[t.aligns[ci]]}>
                  {renderInline(c, `t-${bk}-${ri + 1}-${ci}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default MarkdownPreview;

// ============================================================
// feat-343 — reading-body authoring chrome shared by the Reading editor
// (ReadingDetailForm) and the Notion importer's Reading card. Moved out of
// ReadingDetailForm verbatim so the two surfaces cannot drift. NOT part of the
// renderer dialect above (the Flutter mirror does not care about these).
// ============================================================

export type ReadingEditorMode = 'edit' | 'preview';

/** The body textarea's placeholder (ReadingDetailForm, verbatim). */
export const READING_BODY_PLACEHOLDER =
  'Write the reading here.\n\nBlank lines separate paragraphs. **bold**, *italic*, # headings, - bullet lists, and | pipe | tables | render on mobile-ready text.';

/** The footnote under every reading preview. */
export const READING_PREVIEW_PARITY_NOTE =
  'Preview uses the same markdown subset the mobile app renders (parity).';

/** ADR 0001 D5: the translation pipeline caps a translatable field at 8000
 *  chars; soft-warn past this (advisory only — the item still saves). */
export const READING_BODY_SOFT_WARN_CHARS = 8000;

/** `1,234 characters`, plus the soft-warn suffix past the cap. */
export function readingCharCountText(n: number): string {
  return `${n.toLocaleString()} characters${
    n > READING_BODY_SOFT_WARN_CHARS
      ? ` · over ${READING_BODY_SOFT_WARN_CHARS.toLocaleString()} — translation may skip this field`
      : ''
  }`;
}

/** The Edit ⇄ Preview segmented toggle. */
export const EditPreviewToggle: React.FC<{
  mode: ReadingEditorMode;
  onChange: (mode: ReadingEditorMode) => void;
}> = ({ mode, onChange }) => (
  <div className="inline-flex bg-gray-100 rounded-full p-0.5 text-[11px] font-semibold">
    <button
      type="button"
      aria-pressed={mode === 'edit'}
      onClick={() => onChange('edit')}
      className={`rounded-full px-2.5 py-1 ${mode === 'edit' ? 'bg-white text-ink shadow-sm' : 'text-inkMuted'}`}
    >
      Edit
    </button>
    <button
      type="button"
      aria-pressed={mode === 'preview'}
      onClick={() => onChange('preview')}
      className={`rounded-full px-2.5 py-1 ${mode === 'preview' ? 'bg-white text-ink shadow-sm' : 'text-inkMuted'}`}
    >
      Preview
    </button>
  </div>
);
